import { useAuth0 } from '@auth0/auth0-react';
import { Layout, Menu } from 'antd';
import React, { memo, useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import ApiAccess from '@marketreach/pages/auth/settings/ApiAccess';
import CustomEntity from '@marketreach/pages/auth/settings/CustomEntity';
import { GPT3Settings } from '@marketreach/pages/auth/settings/GPT3Settings';
import SchemaViewer from '@marketreach/pages/auth/settings/SchemaViewer';
import TaskManager from '@marketreach/pages/auth/settings/TaskManager';
import UserManagement from '@marketreach/pages/auth/settings/UserManagement';

import {
  hasPermission,
  SETTINGS_LOCALES_MANAGE_PERMISSIONS,
  SETTINGS_USERS_MANAGE_PERMISSIONS,
} from '../../../utils/permissioins';
import Actions from './Actions';
import LocaleSetting from './ClientManagement';
import './styles.scss';

const { Sider } = Layout;

/**
 * Main settings page
 * @return {JSX.Element}
 * @constructor
 */
const SettingsPage = () => {
  const { user } = useAuth0();
  const { type } = useParams();

  const [collapsed, setCollapsed] = useState(false);
  /**
   * Collapse handler
   */
  const handleToggleClick = () => {
    setCollapsed(!collapsed);
  };

  const [tab, setTab] = useState(
    type ||
      (hasPermission(user, SETTINGS_LOCALES_MANAGE_PERMISSIONS)
        ? 'clients'
        : 'actions')
  );
  /**
   * Menu change handler
   * @param e
   */
  const handleChangeMenu = (e) => {
    setTab(e.key);
  };

  return (
    <div className="ant-pro-grid-content settings-content">
      <Layout>
        <Sider
          width={250}
          collapsedWidth={15}
          collapsible
          collapsed={collapsed}
          onCollapse={handleToggleClick}
          className="side-bar-content"
        >
          {collapsed ? (
            <div className="collapsed-sider-content" />
          ) : (
            <>
              <div className="side-bar-content-title">Settings</div>
              <Menu
                defaultSelectedKeys={[tab]}
                mode="inline"
                onSelect={handleChangeMenu}
              >
                {hasPermission(user, SETTINGS_LOCALES_MANAGE_PERMISSIONS) && (
                  <Menu.Item key="clients">
                    <Link to="/settings/clients">Clients</Link>
                  </Menu.Item>
                )}
                <Menu.Item key="actions">
                  <Link to="/settings/actions">Actions</Link>
                </Menu.Item>
                <Menu.Item key="access">
                  <Link to="/settings/access">API Access</Link>
                </Menu.Item>
                <Menu.Item key="schema">
                  <Link to="/settings/schema">Schema Viewer</Link>
                </Menu.Item>
                {hasPermission(user, SETTINGS_USERS_MANAGE_PERMISSIONS) && (
                  <Menu.Item key="management">
                    <Link to="/settings/management">User Management</Link>
                  </Menu.Item>
                )}
                {hasPermission(user, SETTINGS_USERS_MANAGE_PERMISSIONS) && (
                  <Menu.Item key="task_manager">
                    <Link to="/settings/task_manager">Task Manager</Link>
                  </Menu.Item>
                )}
                <Menu.Item key="custom_entities">
                  <Link to="/settings/custom_entities">Custom Entities</Link>
                </Menu.Item>
                <Menu.Item key="gpt3_settings">
                  <Link to="/settings/openai_settings">GPT3</Link>
                </Menu.Item>
              </Menu>
            </>
          )}
        </Sider>
        <Layout>
          {tab === 'clients' && <LocaleSetting />}
          {tab === 'actions' && <Actions />}
          {tab === 'management' && <UserManagement />}
          {tab === 'access' && <ApiAccess />}
          {tab === 'schema' && <SchemaViewer />}
          {tab === 'custom_entities' && <CustomEntity />}
          {tab === 'task_manager' && <TaskManager />}
          {tab === 'gpt3_settings' && <GPT3Settings />}
        </Layout>
      </Layout>
    </div>
  );
};

export default memo(SettingsPage);
